import React, { useMemo, useState } from 'react';
import { ComposableMap, Geographies, Geography } from 'react-simple-maps';
import { Globe2, MousePointer2 } from 'lucide-react';
import { countryMarkers } from '../data/mock';

const geoUrl = 'https://cdn.jsdelivr.net/npm/world-atlas@2.0.2/countries-110m.json';

const geographyAliases = {
  'United States of America': 'USA',
  'United States': 'USA',
  'Russian Federation': 'Russia',
};

const markerNames = new Set(countryMarkers.map((market) => market.name));

const WorldMap = () => {
  const [hovered, setHovered] = useState(null);
  const [selected, setSelected] = useState('India');
  const activeName = hovered || selected;
  const active = useMemo(() => {
    return countryMarkers.find((market) => market.name === activeName) || {
      name: activeName,
      tag: 'International coordination available through our global compliance network.',
    };
  }, [activeName]);

  return (
    <div className="relative w-full">
      <div className="relative w-full min-h-[330px] md:min-h-0 md:aspect-[21/9] max-h-[560px] rounded-[2rem] bg-white border border-blue-100 overflow-hidden shadow-[0_24px_70px_-45px_rgba(37,99,165,0.5)]">
        <div className="absolute left-5 top-5 z-10 hidden sm:inline-flex items-center gap-2 rounded-full bg-white/90 backdrop-blur border border-blue-100 px-3.5 py-2 text-[10px] uppercase tracking-widest font-bold text-[#2D67A6] shadow-sm pointer-events-none">
          <MousePointer2 className="w-3.5 h-3.5" /> Hover a country
        </div>

        <ComposableMap
          projectionConfig={{ scale: 155, center: [15, 15] }}
          width={980}
          height={420}
          style={{ width: '100%', height: '100%', position: 'relative' }}
        >
          <Geographies geography={geoUrl}>
            {({ geographies }) =>
              geographies.map((geo) => {
                const geographyName = geographyAliases[geo.properties.name] || geo.properties.name;
                const isServed = markerNames.has(geographyName);
                const isPrimary = geographyName === 'India';
                const isActive = activeName === geographyName;

                return (
                  <Geography
                    key={geo.rsmKey}
                    geography={geo}
                    aria-label={geographyName}
                    onMouseEnter={() => setHovered(geographyName)}
                    onMouseMove={() => setHovered(geographyName)}
                    onMouseLeave={() => setHovered(null)}
                    onPointerEnter={() => setHovered(geographyName)}
                    onPointerLeave={() => setHovered(null)}
                    onFocus={() => setHovered(geographyName)}
                    onBlur={() => setHovered(null)}
                    onClick={() => setSelected(geographyName)}
                    style={{
                      default: {
                        fill: isActive ? '#C41E3A' : isPrimary ? '#285F9B' : isServed ? '#3E79B8' : '#548BC4',
                        stroke: 'rgba(255,255,255,0.48)',
                        strokeWidth: 0.55,
                        outline: 'none',
                        cursor: 'pointer',
                        transition: 'fill 180ms ease',
                      },
                      hover: {
                        fill: '#C41E3A',
                        stroke: '#FFFFFF',
                        strokeWidth: 0.9,
                        outline: 'none',
                        cursor: 'pointer',
                      },
                      pressed: {
                        fill: '#A81830',
                        stroke: '#FFFFFF',
                        strokeWidth: 0.9,
                        outline: 'none',
                      },
                    }}
                  />
                );
              })
            }
          </Geographies>
        </ComposableMap>

        {active && (
          <div className="absolute top-4 right-4 md:top-5 md:right-5 bg-white/95 backdrop-blur border border-blue-100 rounded-2xl p-4 max-w-[240px] shadow-xl pointer-events-none">
            <div className="flex items-center gap-2 mb-1.5">
              <span className={`w-2 h-2 rounded-full ${active.primary ? 'bg-[#285F9B]' : 'bg-[#C41E3A]'} animate-pulse`} />
              <span className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">
                {active.primary ? 'Home base' : markerNames.has(active.name) ? 'Coverage market' : 'Global network'}
              </span>
            </div>
            <div className="font-display text-lg font-bold text-neutral-900 leading-tight mb-1">{active.name}</div>
            <div className="text-[11px] text-neutral-600 leading-relaxed">{active.tag}</div>
          </div>
        )}
      </div>

      <div className="mt-5 flex flex-col md:flex-row items-center justify-center gap-4">
        <div className="flex items-center gap-4 bg-white border border-neutral-200 rounded-full px-4 h-9 shadow-sm shrink-0">
          <span className="flex items-center gap-2 text-[11px] text-neutral-700">
            <span className="w-2.5 h-2.5 rounded-full bg-[#285F9B]" /> Home base
          </span>
          <span className="w-px h-4 bg-neutral-200" />
          <span className="flex items-center gap-2 text-[11px] text-neutral-700">
            <span className="w-2.5 h-2.5 rounded-full bg-[#C41E3A]" /> Active region
          </span>
        </div>

        <div className="flex flex-wrap gap-2 justify-center">
          {countryMarkers.map((market) => (
            <button
              type="button"
              key={market.name}
              onMouseEnter={() => setHovered(market.name)}
              onMouseLeave={() => setHovered(null)}
              onFocus={() => setHovered(market.name)}
              onBlur={() => setHovered(null)}
              onClick={() => setSelected(market.name)}
              className={`px-3.5 h-8 rounded-full text-xs font-medium border transition-all ${
                activeName === market.name
                  ? 'bg-[#C41E3A] text-white border-[#C41E3A] shadow-sm'
                  : 'bg-white text-neutral-700 border-neutral-200 hover:border-[#2D67A6] hover:text-[#2D67A6]'
              }`}
            >
              {market.name}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-4 flex justify-center">
        <span className="inline-flex items-center gap-2 text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">
          <Globe2 className="w-3.5 h-3.5 text-[#2D67A6]" /> Select any country surface for regional guidance
        </span>
      </div>
    </div>
  );
};

export default WorldMap;
